import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { getDirtyValues } from 'src/utils/getDirtyValues';
import { ReferenceRateValue } from 'src/features/referenceRateValues/utils/types';
import {
    referenceRateValueFormSchema,
    ReferenceRateValueFormSchemaValues,
} from 'src/features/referenceRateValues/utils/formSchema';
import { useGetReferenceRateValue } from 'src/features/referenceRateValues/api/useGetReferenceRateValue';
import ReferenceRateValueForm from 'src/features/referenceRateValues/components/forms/ReferenceRateValueForm';
import { useGetReferenceRate } from 'src/features/referenceRates/api/useGetReferenceRate';

interface ReferenceRateValueFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedReferenceRateValue?: ReferenceRateValue;
    children?: React.ReactNode;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ReferenceRateValueFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function ReferenceRateValueFormModal<TData>({
    selectedReferenceRateValue,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: ReferenceRateValueFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { referenceRateId = '' } = useParams();

    const { data: referenceRateValue } = useGetReferenceRateValue({
        referenceRateId,
        valueId: selectedReferenceRateValue?.id ?? '',
        config: { enabled: !!referenceRateId && !!selectedReferenceRateValue?.id && isModalOpen },
    });
    // Same key as the dashboard's own query, so this reads the cached rate rather than refetching it.
    const { data: referenceRate } = useGetReferenceRate({
        id: referenceRateId,
        config: { enabled: !!referenceRateId && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            valid_from: referenceRateValue?.valid_from ?? undefined,
            amount: referenceRateValue?.amount ?? undefined,
            source_note: referenceRateValue?.source_note ?? undefined,
        };
    }, [referenceRateValue]);

    const form = useForm<ReferenceRateValueFormSchemaValues>({
        resolver: zodResolver(referenceRateValueFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ReferenceRateValueFormSchemaValues) => {
            const payload = selectedReferenceRateValue
                ? (getDirtyValues(form.formState.dirtyFields, values) as ReferenceRateValueFormSchemaValues)
                : values;

            mutate(
                payload,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [selectedReferenceRateValue, form.formState.dirtyFields, form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ReferenceRateValueForm form={form} onSubmit={onSubmit} referenceRate={referenceRate} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedReferenceRateValue && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ReferenceRateValueFormModal;
